import { router } from 'expo-router';
import React from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    H2Emphasized,
    ScreenContainer,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../../../events/hooks/useViewEvents';
import { useMoneyMapWorkflow } from '../../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../../types/GoWorkflow';

export interface ApexAccountsSuccessProps {
    isPendingInvestigationRun: boolean;
    isWaitingForPartner: boolean;
    canProceedToIncomeSplit: boolean;
}

/**
 * TODO |2026-01-26|: Consolidate with AccountStepSuccess once
 *                    it's rewritten in new smoothie components.
 */
export function ApexAccountsSuccess({
    /* True when accounts are still pending investigation but we're letting the member proceed. */
    isPendingInvestigationRun,
    isWaitingForPartner,
    canProceedToIncomeSplit,
}: ApexAccountsSuccessProps) {
    const { trackViewOnce } = useViewEvents();
    const { proceedForward } = useMoneyMapWorkflow();

    const header = isWaitingForPartner
        ? "You're all set!"
        : isPendingInvestigationRun
          ? 'Your Fruitful Investment accounts are being opened!'
          : 'Your Fruitful Investment accounts are open!';

    const body = isWaitingForPartner
        ? "Waiting for your partner to complete their investment accounts. You'll be notified when everything is ready."
        : isPendingInvestigationRun
          ? "Your accounts are being setup and will be ready shortly. In the meantime, let's confirm your Income Split rules."
          : "Now, let's confirm your Income Split rules.";

    trackViewOnce('Roadmap/Go/ApexAccountsSuccess');

    return (
        <ScreenContainer
            hasBackButton={true}
            onBack={() => {
                router.navigate('/roadmap/go/start');
            }}
        >
            <YStack
                gap={20}
                $md={{ minWidth: 400 }}
                maxWidth={500}
                height="100%"
                minHeight={300}
                justifyContent="space-between"
                paddingTop={64}
                paddingHorizontal={24}
            >
                <YStack flex={1} alignItems="center" gap={32}>
                    <YStack
                        alignItems="center"
                        justifyContent="center"
                        marginBottom={30}
                    >
                        <View marginLeft={25} transform={[{ rotate: '8deg' }]}>
                            <IconTile color="green" useAltColors={true} />
                        </View>
                        <XStack gap={15} marginTop={-10}>
                            <View transform={[{ rotate: '-18deg' }]}>
                                <IconTile color="blue" useAltColors={true} />
                            </View>
                            <View
                                marginTop={10}
                                transform={[{ rotate: '12deg' }]}
                            >
                                <IconTile color="red" useAltColors={true} />
                            </View>
                        </XStack>
                    </YStack>

                    <H2Emphasized textAlign="center" paddingHorizontal={32}>
                        {header}
                    </H2Emphasized>
                    <BodyMediumRegular textAlign="center">
                        {body}
                    </BodyMediumRegular>
                </YStack>
                <YStack>
                    {canProceedToIncomeSplit ? (
                        <Button
                            onPress={() => {
                                proceedForward(
                                    GoWorkflowStepType.APEX_ACCOUNTS,
                                );
                            }}
                            text="Review Income Split rules"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                        />
                    ) : (
                        <Button
                            onPress={() => {
                                router.navigate('/roadmap/go/start');
                            }}
                            text="Back to Money Map"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                        />
                    )}
                </YStack>
            </YStack>
        </ScreenContainer>
    );
}

const IconTile = ({
    color,
    useAltColors = false,
}: {
    color: string;
    useAltColors?: boolean;
}) => {
    return (
        <FruitfulCustomIcon
            icon="fruitful"
            size="lg"
            showShadow={true}
            color={color}
            useAltColors={useAltColors}
        />
    );
};
